import React, { useState, useEffect, useContext, useRef } from 'react';
import EducatorLayout from './components/EducatorLayout';
import api from '../api/api';
import { AuthContext } from '../auth/AuthContext';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';

const getInitials = (name) => {
  if (!name) return '';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const formatTimestamp = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const Spinner = () => (
  <Stack alignItems="center" justifyContent="center" sx={{ py: 3 }}>
    <CircularProgress size={28} />
  </Stack>
);

const EducatorMessages = () => {
  const { token, user } = useContext(AuthContext) ?? {};
  const [contacts, setContacts] = useState([]);
  const [activeChatUser, setActiveChatUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageContent, setMessageContent] = useState('');

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [errorContacts, setErrorContacts] = useState(null);
  const [errorMessages, setErrorMessages] = useState(null);
  const [errorSend, setErrorSend] = useState(null);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!token || !user?._id) return;

    const fetchContacts = async () => {
      setLoadingUsers(true);
      setErrorContacts(null);
      try {
        const { data } = await api.get('/users', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const filtered = (data || []).filter((u) => {
          if (!u || !u._id) return false;
          if (u._id === user._id) return false;
          const role = (u.role || '').toLowerCase().trim();
          return role === 'student' || role === 'parent' || role === 'admin' || role === 'superadmin';
        });
        setContacts(filtered);
        if (filtered.length > 0) setActiveChatUser(filtered[0]);
      } catch (err) {
        console.error('Failed to load contacts:', err);
        setErrorContacts('Failed to load your contacts.');
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchContacts();
  }, [token, user?._id]);

  useEffect(() => {
    if (!activeChatUser) {
      setMessages([]);
      return;
    }

    const fetchMessages = async () => {
      setLoadingMessages(true);
      setErrorMessages(null);
      try {
        const { data } = await api.get(`/messages?withUser=${activeChatUser._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
 setMessages(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load messages:', err);
        setErrorMessages('Failed to load messages.');
        setMessages([]);
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchMessages();
  }, [activeChatUser, token]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (!activeChatUser) return;
    const trimmed = messageContent.trim();
    if (!trimmed) return;

    setSending(true);
    setErrorSend(null);
    try {
      await api.post(
        '/messages/send',
        {
          recipients: [activeChatUser._id],
          content: trimmed,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessageContent('');
      const { data } = await api.get(`/messages?withUser=${activeChatUser._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
 setMessages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to send message:', err);
      setErrorSend('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <EducatorLayout>
      <Box>
        <Card elevation={1} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack spacing={3}>
              <Stack spacing={0.5}>
                <Typography variant="h6" fontWeight={700}>
                  Messages
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Chat with students, parents, and admins.
                </Typography>
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ width: '100%' }}>
                <Card elevation={1} sx={{ borderRadius: 3, width: { xs: '100%', md: 320 } }}>
                  <CardHeader title="Contacts" />
                  <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                    {loadingUsers ? (
                      <Spinner />
 ) : errorContacts ? (
                      <Alert severity="error">{errorContacts}</Alert>
 ) : contacts.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No contacts found.
                      </Typography>
 ) : (
                      <List disablePadding>
                        {contacts.map((contact) => {
                          const isActive = activeChatUser?._id === contact._id;
                          return (
                            <ListItemButton
                              key={contact._id}
                              selected={isActive}
                              onClick={() => setActiveChatUser(contact)}
                              sx={{ borderRadius: 2, mb: 0.5 }}
                            >
                              <ListItemAvatar>
                                <Avatar>{getInitials(contact.name)}</Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={contact.name}
                                secondary={contact.email}
                                primaryTypographyProps={{ noWrap: true }}
                                secondaryTypographyProps={{ noWrap: true }}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    )}
                  </CardContent>
                </Card>

                <Card
                  elevation={1}
                  sx={{ borderRadius: 3, minHeight: 480, display: 'flex', flexDirection: 'column', flex: 1 }}
                >
 <CardHeader title={activeChatUser ? `Chat with ${activeChatUser.name}` : 'Select a contact'} />
                  <Divider />
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
                    <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2, bgcolor: '#f5f5f5' }}>
                      {loadingMessages ? (
                        <Spinner />
                      ) : errorMessages ? (
                        <Alert severity="error">{errorMessages}</Alert>
                      ) : !activeChatUser ? (
                        <Typography variant="body2" color="text.secondary">
                          Choose a contact to begin chatting.
                        </Typography>
                      ) : messages.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No messages yet.
                        </Typography>
                      ) : (
                        <Stack spacing={1.5}>
                          {messages.map((msg) => {
                            const mine = msg.sender?._id === user?._id;
                            const initials = getInitials(msg.sender?.name || '');
                            return (
                              <Stack
                                key={msg._id || msg.createdAt}
                                direction="row"
                                spacing={1}
 justifyContent={mine ? 'flex-end' : 'flex-start'}
                                alignItems="flex-end"
                              >
                                {!mine && <Avatar>{initials}</Avatar>}
                                <Box
                                  sx={{
                                    maxWidth: '75%',
                                    px: 1.75,
                                    py: 1.25,
                                    bgcolor: mine ? 'success.light' : 'background.paper',
                                    borderRadius: mine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                    boxShadow: 1,
                                  }}
                                >
                                  <Typography variant="body2">{msg.content}</Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}
                                  >
                                    {formatTimestamp(msg.createdAt)}
                                  </Typography>
                                </Box>
                                {mine && <Avatar>{initials}</Avatar>}
                              </Stack>
                            );
                          })}
                          <Box ref={messagesEndRef} />
                        </Stack>
                      )}
                    </Box>

                    <Divider />

                    <Box sx={{ p: 2 }}>
                      {errorSend && (
                        <Alert severity="error" sx={{ mb: 1 }}>
                          {errorSend}
                        </Alert>
                      )}
                      <Stack direction="row" spacing={1.5} alignItems="flex-end">
                        <TextField
                          multiline
                          minRows={1}
                          maxRows={4}
                          fullWidth
                          value={messageContent}
                          onChange={(e) => setMessageContent(e.target.value)}
                          disabled={sending || !activeChatUser}
                          placeholder={activeChatUser ? 'Type a message' : 'Select a student first'}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              if (!sending) handleSendMessage();
                            }
                          }}
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleSendMessage}
                          disabled={sending || !activeChatUser}
                          startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendRoundedIcon />}
                          sx={{ minWidth: 56 }}
                        >
                          {sending ? 'Sending' : 'Send'}
                        </Button>
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </EducatorLayout>
  );
};

export default EducatorMessages;
